import { format } from 'date-fns'
import { RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMyBadgeToken } from '@/api/queries/virtual-badge-queries'
import logoRip from '@/assets/KAEFER_Logo_www_pos.svg'
import { BrandedQrCode } from '@/components/commons/branded-qr-code'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useNow } from '@/hooks/use-now'
import { cn } from '@/lib/utils'

const QR_SIZE = 220

function formatRemaining(ms: number) {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000))
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

interface VirtualBadgeCardProps {
	enabled?: boolean
	className?: string
}

export const VirtualBadgeCard = ({
	enabled = true,
	className,
}: VirtualBadgeCardProps) => {
	const { t } = useTranslation()
	const { data, isPending, isError, isFetching, refetch } =
		useMyBadgeToken(enabled)
	const now = useNow()

	const remainingMs = data ? new Date(data.expiresAt).getTime() - now : 0
	const isExpired = !!data && remainingMs <= 0

	return (
		<div
			className={cn(
				'mx-auto flex w-full max-w-xs flex-col items-center gap-4 rounded-xl border-2 border-kaefer-dark-grey bg-white px-6 py-6 text-kaefer-dark-grey shadow-sm',
				className,
			)}
		>
			<img
				alt="KAEFER RIP Serviços Industriais"
				className="h-auto w-48"
				src={logoRip}
			/>

			<div
				className="relative flex items-center justify-center"
				style={{ width: QR_SIZE, height: QR_SIZE }}
			>
				{isPending || (isError && !data) ? (
					<Skeleton className={cn('size-full', isError && 'animate-none')} />
				) : (
					<BrandedQrCode
						aria-label={t('virtualBadge.qrLabel')}
						className={cn(
							'transition-[filter]',
							isExpired && 'opacity-30 blur-sm',
						)}
						marginSize={0}
						size={QR_SIZE}
						value={data.qrValue}
					/>
				)}

				{(isExpired || (isError && !data)) && (
					<div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center">
						<p className="font-semibold text-sm">
							{isExpired
								? t('virtualBadge.expired')
								: t('virtualBadge.loadError')}
						</p>
						<Button
							disabled={isFetching}
							onClick={() => refetch()}
							size="sm"
							type="button"
						>
							<RefreshCw
								className={cn('size-4', isFetching && 'animate-spin')}
							/>
							{t('virtualBadge.generateNew')}
						</Button>
					</div>
				)}
			</div>

			<div className="flex w-full flex-col items-center gap-1 text-center">
				{data ? (
					<>
						<p className="font-bold text-2xl tracking-wider">{data.badge}</p>
						<p className="font-semibold text-sm uppercase leading-tight">
							{data.name}
						</p>
						{data.jobRole && (
							<p className="font-bold text-base uppercase leading-tight">
								{data.jobRole}
							</p>
						)}
					</>
				) : (
					<>
						<Skeleton className="h-7 w-36" />
						<Skeleton className="h-4 w-52" />
						<Skeleton className="h-5 w-40" />
					</>
				)}
			</div>

			{data && (
				<div className="flex flex-col items-center gap-0.5 text-center text-[11px] text-kaefer-medium-grey leading-tight">
					<p>
						{t('virtualBadge.generatedAt')}:{' '}
						{format(new Date(data.issuedAt), 'dd/MM/yy HH:mm')}
					</p>
					<p className="font-mono">
						{t('virtualBadge.verificationCode')}: {data.verificationCode}
					</p>
					{!isExpired && (
						<p
							className={cn(
								'mt-1 font-medium',
								remainingMs < 1 * 60 * 1000 && 'text-kaefer-red',
							)}
						>
							{t('virtualBadge.validUntil', {
								time: format(new Date(data.expiresAt), 'HH:mm'),
								remaining: formatRemaining(remainingMs),
							})}
						</p>
					)}
				</div>
			)}
		</div>
	)
}
